import { useEffect, useRef } from "react";

interface UseInfiniteScrollProps {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export function useInfiniteScroll({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: UseInfiniteScrollProps) {
  const observerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!observerRef.current) return;

    let observer: IntersectionObserver | null = null;
    let currentRow: HTMLDivElement | null = null;

    const timer = setTimeout(() => {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1, rootMargin: "200px" },
      );

      currentRow = observerRef.current;
      if (currentRow) observer.observe(currentRow);
    }, 300); // delay to avoid initial false positive

    return () => {
      clearTimeout(timer);
      if (observer && currentRow) {
        observer.unobserve(currentRow);
        observer.disconnect();
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return observerRef;
}
